import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Camera, X } from 'lucide-react';
import api, { API } from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { formatCurrency } from '../../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const STATUSES = ['Open', 'Approved', 'In Progress', 'Waiting for Parts', 'Completed', 'Cancelled'];
const RATE = 170;
const inp = 'w-full bg-muted/40 border border-border rounded-md px-2.5 py-2 text-[13px] outline-none focus:border-primary transition-colors';

const emptyLabor = () => ({ id: `l${Date.now()}${Math.random().toString(16).slice(2, 6)}`, description: '', hours: '', rate: RATE });
const emptyPart = () => ({ id: `p${Date.now()}${Math.random().toString(16).slice(2, 6)}`, name: '', part_number: '', qty: 1, unit_cost: '', vendor: '', ordered: false, received: false });

export const ReconDialog = ({ open, onClose, onSaved, inspectionId, item, existing }) => {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [upl, setUpl] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(existing ? {
      issue: existing.issue, action: existing.action, priority: existing.priority, status: existing.status,
      labor: existing.labor?.length ? existing.labor : [emptyLabor()], parts: existing.parts || [],
      other_cost: existing.other_cost || '', notes: existing.notes || '', photos: existing.photos || [],
    } : {
      issue: item?.label ? `${item.label}${item.finding ? ` — ${item.finding}` : ''}` : '', action: '',
      priority: item?.state === 'repair' ? 'High' : 'Medium', status: 'Open',
      labor: [emptyLabor()], parts: [], other_cost: '', notes: '', photos: [],
    });
  }, [open, existing, item]);

  if (!open || !f) return null;

  const laborTotal = f.labor.reduce((s, l) => s + (Number(l.hours) || 0) * (Number(l.rate) || 0), 0);
  const partsTotal = f.parts.reduce((s, p) => s + (Number(p.qty) || 0) * (Number(p.unit_cost) || 0), 0);
  const total = laborTotal + partsTotal + (Number(f.other_cost) || 0);

  const save = async () => {
    if (!f.issue.trim()) { toast({ title: 'Descreva o problema (Issue)' }); return; }
    setSaving(true);
    try {
      const payload = {
        issue: f.issue, action: f.action, priority: f.priority, notes: f.notes,
        labor: f.labor.filter((l) => l.description || l.hours).map((l) => ({ ...l, hours: Number(l.hours) || 0, rate: Number(l.rate) || 0, total: (Number(l.hours) || 0) * (Number(l.rate) || 0) })),
        parts: f.parts.filter((p) => p.name).map((p) => ({ ...p, qty: Number(p.qty) || 0, unit_cost: Number(p.unit_cost) || 0, total: (Number(p.qty) || 0) * (Number(p.unit_cost) || 0) })),
        other_cost: Number(f.other_cost) || 0,
      };
      if (existing) {
        await api.put(`/recon/${existing.id}`, { ...payload, status: f.status });
      } else {
        await api.post('/recon', {
          ...payload, inspection_id: inspectionId,
          source_item_key: item?.key || '', source_item_label: item?.label || '', source_finding: item?.finding || '',
        });
      }
      toast({ title: existing ? 'Recon atualizado' : 'Recon criado', description: formatCurrency(total) });
      onSaved && onSaved();
      onClose();
    } catch (e) { toast({ title: 'Erro ao salvar', description: e.response?.data?.detail || '' }); }
    finally { setSaving(false); }
  };

  const addPhoto = async (files) => {
    if (!existing || !files?.length) return;
    setUpl(true);
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await api.post(`/recon/${existing.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setF((p) => ({ ...p, photos: [...p.photos, data] }));
      } catch (e) { toast({ title: 'Erro na foto', description: e.response?.data?.detail || '' }); }
    }
    setUpl(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display font-bold text-lg uppercase tracking-tight" data-testid="recon-dialog-title">
            {existing ? 'Editar item de Recon' : 'Adicionar ao Recon'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {item?.label && !existing && (
            <p className="text-[11px] t-label">Origem: <span className="text-foreground/80">{item.label}</span>{item.finding ? ` · ${item.finding}` : ''}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="ent-label block mb-1">Issue (o que foi encontrado)</label><input data-testid="recon-issue" className={inp} value={f.issue} onChange={(e) => setF({ ...f, issue: e.target.value })} /></div>
            <div><label className="ent-label block mb-1">Recommended action</label><input data-testid="recon-action" className={inp} value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} placeholder="Ex: Replace front brake pads" /></div>
            <div>
              <label className="ent-label block mb-1">Prioridade</label>
              <div className="flex gap-1.5">
                {PRIORITIES.map((p) => (
                  <button key={p} data-testid={`recon-priority-${p}`} onClick={() => setF({ ...f, priority: p })}
                    className={`ent-btn ent-btn-sm flex-1 justify-center ${f.priority === p ? 'ent-btn-primary' : ''}`}>{p}</button>
                ))}
              </div>
            </div>
            {existing && (
              <div><label className="ent-label block mb-1">Status</label>
                <select data-testid="recon-status" className={inp} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} className="bg-popover">{s}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* labor */}
          <div className="rounded-md border border-border">
            <div className="ent-colhead"><span className="ent-coltitle">Labor</span><span className="ent-pill">{formatCurrency(laborTotal)}</span>
              <button data-testid="recon-add-labor" onClick={() => setF({ ...f, labor: [...f.labor, emptyLabor()] })} className="ent-btn ent-btn-sm ml-auto"><Plus className="w-3 h-3" /> Linha</button>
            </div>
            <div className="p-2 space-y-2">
              {f.labor.map((l, idx) => (
                <div key={l.id} className="grid grid-cols-[1fr_70px_80px_80px_28px] gap-2 items-center">
                  <input data-testid={`recon-labor-desc-${idx}`} className={inp} placeholder="Descrição" value={l.description} onChange={(e) => setF({ ...f, labor: f.labor.map((x) => x.id === l.id ? { ...x, description: e.target.value } : x) })} />
                  <input data-testid={`recon-labor-hours-${idx}`} type="number" step="0.1" className={inp} placeholder="hrs" value={l.hours} onChange={(e) => setF({ ...f, labor: f.labor.map((x) => x.id === l.id ? { ...x, hours: e.target.value } : x) })} />
                  <input type="number" className={inp} placeholder="rate" value={l.rate} onChange={(e) => setF({ ...f, labor: f.labor.map((x) => x.id === l.id ? { ...x, rate: e.target.value } : x) })} />
                  <span className="num text-[12px] font-semibold text-right">{formatCurrency((Number(l.hours) || 0) * (Number(l.rate) || 0))}</span>
                  <button onClick={() => setF({ ...f, labor: f.labor.filter((x) => x.id !== l.id) })} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* parts */}
          <div className="rounded-md border border-border">
            <div className="ent-colhead"><span className="ent-coltitle">Parts</span><span className="ent-pill">{formatCurrency(partsTotal)}</span>
              <button data-testid="recon-add-part" onClick={() => setF({ ...f, parts: [...f.parts, emptyPart()] })} className="ent-btn ent-btn-sm ml-auto"><Plus className="w-3 h-3" /> Peça</button>
            </div>
            <div className="p-2 space-y-2">
              {f.parts.length === 0 && <p className="ent-empty">Nenhuma peça</p>}
              {f.parts.map((p, idx) => (
                <div key={p.id} className="grid grid-cols-[1fr_100px_56px_80px_90px_70px_28px] gap-2 items-center">
                  <input data-testid={`recon-part-name-${idx}`} className={inp} placeholder="Peça" value={p.name} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, name: e.target.value } : x) })} />
                  <input className={inp} placeholder="Part #" value={p.part_number} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, part_number: e.target.value } : x) })} />
                  <input type="number" className={inp} placeholder="qtd" value={p.qty} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, qty: e.target.value } : x) })} />
                  <input data-testid={`recon-part-cost-${idx}`} type="number" step="0.01" className={inp} placeholder="unit $" value={p.unit_cost} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, unit_cost: e.target.value } : x) })} />
                  <input className={inp} placeholder="Vendor" value={p.vendor} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, vendor: e.target.value } : x) })} />
                  <div className="flex flex-col gap-0.5 text-[10px]">
                    <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={!!p.ordered} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, ordered: e.target.checked } : x) })} className="accent-blue-500" /> Ordered</label>
                    <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={!!p.received} onChange={(e) => setF({ ...f, parts: f.parts.map((x) => x.id === p.id ? { ...x, received: e.target.checked } : x) })} className="accent-emerald-500" /> Received</label>
                  </div>
                  <button onClick={() => setF({ ...f, parts: f.parts.filter((x) => x.id !== p.id) })} className="ent-btn ent-btn-icon border-0 hover:text-[hsl(var(--bad))]"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
            <div><label className="ent-label block mb-1">Outros custos ($)</label><input data-testid="recon-other" type="number" step="0.01" className={inp} value={f.other_cost} onChange={(e) => setF({ ...f, other_cost: e.target.value })} /></div>
            <div><label className="ent-label block mb-1">Notas</label><input className={inp} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
          </div>

          {existing && (
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="ent-label">Fotos</label>
                <label className="ent-btn ent-btn-sm cursor-pointer"><Camera className="w-3 h-3" /> {upl ? 'Enviando...' : 'Adicionar'}
                  <input data-testid="recon-photo-input" type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addPhoto(e.target.files); e.target.value = ''; }} />
                </label>
              </div>
              <div className="flex gap-2 flex-wrap">
                {f.photos.map((p) => (
                  <img key={p.id} src={`${API}/files/${p.path}`} alt="" className="w-16 h-16 object-cover rounded border border-border" />
                ))}
                {f.photos.length === 0 && <p className="text-[11px] t-label">Sem fotos</p>}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-md border border-border bg-muted/15 px-3 py-2.5">
            <span className="ent-label">Recon total</span>
            <span className="num font-display font-black text-xl" data-testid="recon-total">{formatCurrency(total)}</span>
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="ent-btn"><X className="w-3.5 h-3.5" /> Cancelar</button>
          <button data-testid="recon-save" onClick={save} disabled={saving} className="ent-btn ent-btn-primary">{saving ? 'Salvando...' : existing ? 'Salvar alterações' : 'Criar item de Recon'}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconDialog;
